import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, RotateCw, FileCheck2 } from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';
import StatusIndicator from './StatusIndicator';

/**
 * Daily Summary Panel
 *
 * Two responsibilities in one component:
 *
 *   1. Fetch & display a (site, date) FINAL daily summary from
 *      GET /api/oim/daily-summary?site_id=&date=
 *      Returns { found, data: { total_daily_energy, total_daily_production,
 *        total_daily_cost, energy_intensity, cost_intensity, currency,
 *        product, status, flags, ... } }
 *
 *   2. Trigger end-of-day reconciliation via
 *      POST /api/oim/reconcile  body: { site_id, target_date }
 *      and refresh the panel + the parent dashboard on success.
 *
 * Defaults the date to "yesterday (UTC)" — the most common reconcile target.
 *
 * Props:
 *   siteId:           string   (required)
 *   currency:         string   (display only — backend returns its own)
 *   onReconciled:     fn       called after a successful reconcile so the
 *                              parent can refresh the main dashboard / chart
 */

const yesterdayIso = () => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

const fmt = (v, digits = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits }));

const DailySummaryPanel = ({ siteId, currency = 'INR', onReconciled }) => {
  const { showSuccess, showError } = useAlert();

  const [targetDate, setTargetDate] = useState(yesterdayIso());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!siteId || !targetDate) {
      setSummary(null);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ site_id: siteId, date: targetDate });
      const response = await axios.get(`${API}/oim/daily-summary?${params.toString()}`);
      setSummary(response.data);
    } catch (err) {
      console.error('OIM daily-summary fetch failed', err);
      const msg = (err.response && err.response.data && err.response.data.detail) || 'Failed to load daily summary';
      showError(msg);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [siteId, targetDate, showError]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleReconcile = async () => {
    if (!siteId || !targetDate) {
      showError('Site and date are required');
      return;
    }
    setReconciling(true);
    try {
      const response = await axios.post(`${API}/oim/reconcile`, {
        site_id: siteId,
        target_date: targetDate,
      });
      const d = response.data && response.data.data;
      showSuccess(
        d
          ? `Reconciled ${targetDate}: ${d.daily_rows_written} daily row(s), ${d.hourly_rows_finalised} hourly row(s) → FINAL`
          : `Reconciled ${targetDate}`
      );
      await fetchSummary();
      if (onReconciled) onReconciled();
    } catch (err) {
      console.error('OIM reconcile failed', err);
      const msg = (err.response && err.response.data && err.response.data.detail) || 'Reconcile failed';
      showError(msg);
    } finally {
      setReconciling(false);
    }
  };

  const found = summary && summary.found;
  const data = (found && summary.data) || null;
  const summaryStatus = data?.status || 'EMPTY';
  const displayCurrency = data?.currency || currency || 'INR';

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileCheck2 className="w-5 h-5 text-gray-700" />
          <h2 className="text-lg font-semibold text-gray-900">Daily Summary</h2>
          {found && <StatusIndicator status={summaryStatus} small />}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-md bg-white">
            <Calendar className="w-4 h-4 text-gray-500" />
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="text-sm bg-transparent focus:outline-none"
              max={yesterdayIso()}
              title="Target date for reconcile / daily summary (UTC)"
            />
          </div>
          <button
            type="button"
            onClick={fetchSummary}
            disabled={loading || !siteId}
            className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            title="Refresh daily summary"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={handleReconcile}
            disabled={reconciling || !siteId || !targetDate}
            className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded-md text-sm font-medium"
            title="Run end-of-day reconciliation for this date"
          >
            <RotateCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} />
            {reconciling ? 'Reconciling…' : 'Reconcile'}
          </button>
        </div>
      </div>

      {!siteId && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3">
          Select a site to see its daily summary.
        </div>
      )}

      {siteId && !found && !loading && (
        <div className="text-sm text-gray-600 bg-gray-50 border border-dashed border-gray-300 rounded p-4 text-center">
          No FINAL summary on record for <strong>{targetDate}</strong>. Click <strong>Reconcile</strong> to compute it.
          {summary && summary.message && <div className="text-xs mt-1 text-gray-500">{summary.message}</div>}
        </div>
      )}

      {siteId && found && data && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
            <Stat label="Energy Intensity" value={fmt(data.energy_intensity, 4)} unit="kWh/kL" />
            <Stat label="Cost Intensity" value={fmt(data.cost_intensity, 4)} unit={`${displayCurrency}/kL`} />
            <Stat label="Energy (total)" value={fmt(data.total_daily_energy)} unit="kWh" />
            <Stat label="Production (total)" value={fmt(data.total_daily_production)} unit="kL" />
            <Stat label="Cost (total)" value={fmt(data.total_daily_cost)} unit={displayCurrency} />
          </div>

          <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
            {data.product && <span>Product: <strong>{data.product}</strong></span>}
            {data.updated_at && <span>Updated: {new Date(data.updated_at).toLocaleString()}</span>}
            {data.flags && Object.keys(data.flags).length > 0 && (
              <span>
                Flags:{' '}
                {Object.keys(data.flags).map((k) => (
                  <code key={k} className="ml-1 px-1 py-0.5 bg-amber-50 text-amber-800 rounded">{k}</code>
                ))}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, unit }) => (
  <div className="bg-gray-50 border border-gray-200 rounded p-3">
    <div className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">{label}</div>
    <div className="mt-0.5 flex items-baseline gap-1">
      <span className="text-lg font-semibold text-gray-900">{value}</span>
      {unit && <span className="text-xs text-gray-500">{unit}</span>}
    </div>
  </div>
);

export default DailySummaryPanel;
